import {
  BadgeCheck,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  PenTool,
  Phone,
  ShieldCheck,
  Users,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { mailHref, phoneHref, whatsappHref } from './contentUtils.js';

const advantageIcons = {
  penTool: PenTool,
  users: Users,
  badgeCheck: BadgeCheck
};

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="section-title">
      {eyebrow ? <span>{eyebrow}</span> : null}
      <h2>{title}</h2>
    </div>
  );
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg className="messenger-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20.5 11.7a8.5 8.5 0 0 1-12.6 7.4L3.5 20.5l1.4-4.2a8.5 8.5 0 1 1 15.6-4.6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M8.1 7.7c.2-.4.4-.4.8-.4h.4c.2 0 .4.1.5.4l.8 2c.1.3.1.5-.1.7l-.6.7c-.2.2-.1.4 0 .6.6 1.1 1.5 2 2.7 2.6.2.1.4.2.6 0l.8-1c.2-.2.4-.3.7-.2l2 .9c.3.1.4.3.4.6 0 .5-.2 1.5-.8 2-.6.5-1.4.8-2.4.6-1.1-.2-2.5-.8-4.1-2.2-1.3-1.2-2.3-2.7-2.7-3.8-.4-1.1-.1-2.5.3-3.1l.7-.4Z" fill="currentColor" />
    </svg>
  );
}

function MaxIcon({ size = 18 }) {
  return (
    <svg className="messenger-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="3" width="19" height="16" rx="6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 15V9l5 4 5-4v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m8 19-2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const pickupPoints = [
  { address: 'МО, Реутов, метро Новокосино, Южная улица, 10А', phone: '8-977-130-22-09' },
  { address: 'МО, Реутов, метро Новокосино, ул. Котовского, 12, ТЦ «Карина»', phone: '8-962-931-02-46' },
  { address: 'мкр. Железнодорожный, станция Железнодорожная, ул. Советская, 1е, ТЦ «Атлантик»', phone: '8-903-007-47-01' },
  { address: 'мкр. Железнодорожный, станция Железнодорожная, ул. Пролетарская, д. 1А, ТЦ «Океан»', phone: '8-925-730-12-52' }
];

function ContactLinks({ contacts, compact = false }) {
  return (
    <div className={compact ? 'contact-links compact' : 'contact-links'}>
      {contacts.phones.map((phone) => (
        <a href={phoneHref(phone)} key={phone}>
          <Phone size={18} />
          {phone}
        </a>
      ))}
      <a className="messenger-link whatsapp-link" href={whatsappHref(contacts.whatsapp)} target="_blank" rel="noreferrer">
        <WhatsAppIcon size={18} />
        <strong className="messenger-name">WhatsApp</strong>
        <span className="messenger-number">{contacts.whatsapp}</span>
      </a>
      {contacts.maxUrl ? (
        <a className="messenger-link max-link" href={contacts.maxUrl} target="_blank" rel="noreferrer">
          <MaxIcon size={18} />
          <strong className="messenger-name">MAX</strong>
          <span className="messenger-number">{contacts.whatsapp}</span>
        </a>
      ) : null}
    </div>
  );
}

export function Site({ content }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeGalleryItem, setActiveGalleryItem] = useState(null);

  useEffect(() => {
    if (!activeGalleryItem) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setActiveGalleryItem(null);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeGalleryItem]);

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label={content.brand.name}>
          <img src={content.brand.logoUrl} alt="Логотип гравёрно-художественной мастерской" width="72" height="72" />
          <span>
            <strong>{content.brand.name}</strong>
            <small>{content.brand.subtitle}</small>
          </span>
        </a>

        <button className="icon-button nav-toggle" type="button" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <nav className={menuOpen ? 'main-nav open' : 'main-nav'} aria-label="Основная навигация">
          {content.navigation.map((item) => (
            <a href={item.href} key={item.href} onClick={() => setMenuOpen(false)}>
              {item.label}
            </a>
          ))}
        </nav>

        <ContactLinks contacts={content.contacts} compact />
      </header>

      <main id="top">
        <section className="hero-section">
          <div className="hero-bg" style={{ backgroundImage: `url(${content.hero.imageUrl})` }} role="img" aria-label="Художественно гравированный часовой механизм" />
          <div className="hero-content">
            <p className="hero-eyebrow">{content.hero.eyebrow}</p>
            <h1>{content.hero.title}</h1>
            <p>{content.hero.description}</p>
            <div className="hero-actions">
              <a className="button primary" href={whatsappHref(content.contacts.whatsapp)} target="_blank" rel="noreferrer">
                {content.hero.primaryCta}
                <Phone size={19} />
              </a>
              <a className="button ghost" href="#gallery">
                {content.hero.secondaryCta}
              </a>
            </div>
          </div>
        </section>

        <section className="services-section section-pad" id="services">
          <SectionTitle title="Услуги гравировки" />
          <div className="services-grid">
            {content.services.map((service) => (
              <article className="service-card" key={service.id}>
                <img src={service.imageUrl} alt={service.title} loading="lazy" decoding="async" />
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="gallery-section" id="gallery">
          <SectionTitle title="Примеры работ" />
          <div className="gallery-grid">
            {content.gallery.map((item) => (
              <figure
                key={item.id}
                className="gallery-item"
                onClick={() => setActiveGalleryItem(item)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActiveGalleryItem(item);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Открыть изображение: ${item.title}`}
              >
                <img src={item.imageUrl} alt={item.title} loading="lazy" decoding="async" />
                <figcaption>{item.title}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {activeGalleryItem ? (
          <div
            className="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={activeGalleryItem.title}
            onClick={() => setActiveGalleryItem(null)}
          >
            <button
              className="gallery-lightbox-close"
              type="button"
              aria-label="Закрыть изображение"
              onClick={() => setActiveGalleryItem(null)}
            >
              <X size={28} />
            </button>
            <div className="gallery-lightbox-content" onClick={(event) => event.stopPropagation()}>
              <img src={activeGalleryItem.imageUrl} alt={activeGalleryItem.title} />
              <p>{activeGalleryItem.title}</p>
            </div>
          </div>
        ) : null}

        <section className="materials-band" id="materials">
          <SectionTitle title="Работаем с различными материалами" />
          <div className="materials-row">
            {content.materials.map((material) => (
              <div className="material-item" key={material.id}>
                <img src={material.imageUrl} alt={`Гравировка: материал ${material.name}`} loading="lazy" decoding="async" />
                <span>{material.name}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bottom-info section-pad">
          <article className="about-panel" id="about">
            <h2>{content.about.title}</h2>
            <strong>{content.about.accent}</strong>
            <p>{content.about.description}</p>
            <div className="advantage-row">
              {content.about.advantages.map((advantage) => {
                const Icon = advantageIcons[advantage.icon] || ShieldCheck;
                return (
                  <span key={advantage.title}>
                    <Icon size={24} />
                    {advantage.title}
                  </span>
                );
              })}
            </div>
          </article>

          <article className="contact-panel" id="contacts">
            <h2>Свяжитесь с мастером</h2>
            <ContactLinks contacts={content.contacts} />
            <a href={mailHref(content.contacts.email)}>
              <Mail size={18} />
              {content.contacts.email}
            </a>
          </article>

          <article className="cta-panel">
            <h2>Нужна гравировка?</h2>
            <p>Напишите или позвоните — обсудим задачу, материал и сроки.</p>
            <a className="button whatsapp" href={whatsappHref(content.contacts.whatsapp)} target="_blank" rel="noreferrer">
              <WhatsAppIcon size={19} />
              Написать в WhatsApp
            </a>
            {content.contacts.maxUrl ? (
              <a className="button max" href={content.contacts.maxUrl} target="_blank" rel="noreferrer">
                <MaxIcon size={19} />
                Написать в MAX
              </a>
            ) : null}
          </article>

          <article className="address-panel" aria-labelledby="addresses-title">
            <div className="address-panel-heading">
              <MapPin size={25} />
              <div>
                <h2 id="addresses-title">Адреса мастерской и приёма заказов</h2>
                <p>Выберите удобный адрес для передачи изделия мастеру.</p>
              </div>
            </div>
            <div className="workshop-address">
              <span>Художественная мастерская «Чёрная Белка»</span>
              <strong>Носовихинское шоссе, вл. 2, линия 19</strong>
            </div>
            <h3>Адреса приёма заказов по гравировке</h3>
            <div className="pickup-grid">
              {pickupPoints.map((point, index) => (
                <div className="pickup-card" key={point.address}>
                  <span className="pickup-number">{index + 1}</span>
                  <p>{point.address}</p>
                  <a href={phoneHref(point.phone)}>
                    <Phone size={17} />
                    {point.phone}
                  </a>
                </div>
              ))}
            </div>
          </article>
        </section>
      </main>

      <footer className="footer">
        <span>{content.brand.shortName}</span>
        <small>{content.footer.copyright}</small>
        <small>{content.footer.note}</small>
      </footer>
    </div>
  );
}
